import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const FONT_LINK_ID = "terms-of-use-fonts";

export default function TermsOfUsePage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Snap Solutions | Terms of Use";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) {
      meta.setAttribute("content", "Snap Solutions — Terms of Use.");
    }

    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600;700&display=swap";
      document.head.appendChild(link);
    }

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) {
        meta.setAttribute("content", prevDesc || "");
      }
    };
  }, []);

  const logoSrc = `${process.env.PUBLIC_URL}/assets/img/snap-logo-new.png`;

  return (
    <div className="terms-of-use-page">
      <div className="terms-of-use-wrap">
        <Link to="/login" className="terms-back-to-login" aria-label="Back to sign in">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to sign in
        </Link>

        <header className="terms-top-header">
          <div className="terms-brand-logo">
            <img src={logoSrc} alt="Snap Solutions Logo" />
          </div>
          <div className="terms-header-meta">
            <span className="terms-header-meta-label">Legal &amp; Compliance</span>
          </div>
        </header>

        <section className="terms-hero">
          <span className="terms-badge-pill">Legal Notice</span>
          <h1>
            Snap Solutions Platform
            <br />
            Terms of Use
          </h1>
          <div className="terms-hero-meta">
            <span>Effective Date: May 5, 2026</span>
            <span>Last Modified: May 5, 2026</span>
            <span>Version: 1.0</span>
          </div>
        </section>

        <section className="terms-card">
          <div className="terms-legal-content">
            <h2>1. Acceptance and Binding Agreement</h2>
            <h3>1.1 Agreement to Terms</h3>
            <p>
              By clicking “I Agree,” accessing your account, or otherwise using the Snap Solutions platform and any
              related modules, portals, dashboards, workflows, integrations, features, or services (collectively, the
              Platform), you, as an individual user, agree to be legally bound by these Terms of Use (Terms).
            </p>
            <p>If you do not agree to these Terms, you must not access or use the Platform.</p>

            <h3>1.2 Electronic Acceptance Record</h3>
            <p>
              Your agreement to these Terms is electronically recorded, time-stamped, version-controlled, and associated
              with your user account. Snap Solutions Inc. (Snap, we, us, or our) may retain records of your acceptance
              for compliance, audit, security, evidentiary, and legal purposes.
            </p>

            <h3>1.3 Individual User Agreement</h3>
            <p>
              These Terms govern your individual conduct as an authorized user of the Platform. These Terms do not
              create a direct commercial subscription relationship between you and Snap.
            </p>

            <h2>2. Who These Terms Apply To</h2>
            <h3>2.1 Authorized Users</h3>
            <p>
              These Terms govern your individual access to and use of the Platform as an Authorized User. “You” and
              “your” refer to you as an individual, not the organization that has contracted with Snap for access to the
              Platform.
            </p>
            <p>
              The organization that has contracted with Snap for access to the Platform is referred to in these Terms
              as the Customer.
            </p>

            <h3>2.2 Two Authorized User Types</h3>
            <p>
              The Platform supports two distinct categories of Authorized Users. Certain provisions of these Terms apply
              differently depending on which category applies to you.
            </p>

            <h3>2.3 Type 1 — Clinical and Administrative Users</h3>
            <p>
              A Clinical and Administrative User means a healthcare provider, nurse, physician assistant, medical
              assistant, administrator, billing user, coordinator, staff member, contractor, or other individual employed
              by or acting on behalf of the Customer who accesses the Platform to document, manage, administer,
              coordinate, bill, or support clinical care, occupational medicine workflows, employer services, revenue
              cycle functions, or administrative operations.
            </p>

            <h3>2.4 Type 2 — Employer Portal Users</h3>
            <p>
              An Employer Portal User means an individual employed by or acting on behalf of an employer-client whose
              employees receive occupational health, testing, or workforce health services from the Customer, and who
              has been granted access to the Employer Command Center or other employer-facing Platform features by the
              Customer.
            </p>
            <p>
              Employer Portal Users may access the Platform only to review employer-authorized occupational health
              outputs, scheduling information, compliance records, work status information, drug and alcohol testing
              results, DOT medical certificate information, invoices, or other employer-service data made available by
              the Customer through the Platform.
            </p>

            <h3>2.5 Customer Controls Access</h3>
            <p>
              In all cases, access to the Platform is authorized, configured, and controlled by the Customer. The
              Customer is the contracting party under the Master Subscription Agreement, Business Associate Agreement,
              and any applicable Order Form with Snap.
            </p>
            <p>
              These Terms do not make you a party to those agreements and do not create any third-party beneficiary
              rights in your favor.
            </p>

            <h2>3. Order of Precedence</h2>
            <h3>3.1 Corporate Agreements Govern</h3>
            <p>
              These Terms supplement but do not replace or modify the Master Subscription Agreement (MSA), Business
              Associate Agreement (BAA), Order Form, or any other executed agreement between Snap and the Customer.
            </p>
            <p>
              In the event of any conflict between these Terms and the MSA, BAA, Order Form, or other executed agreement
              between Snap and the Customer:
            </p>
            <ul>
              <li>
                The BAA governs with respect to Protected Health Information, HIPAA obligations, and Business Associate
                obligations.
              </li>
              <li>
                The MSA governs with respect to commercial terms, service scope, fees, warranties, disclaimers, Customer
                responsibilities, data rights, service availability, indemnity, limitation of liability, and remedies.
              </li>
            </ul>
            <p>
              These Terms govern only your individual conduct as an Authorized User, subject to the hierarchy above.
            </p>

            <h3>3.2 No Expansion of Rights</h3>
            <p>
              These Terms may not be invoked by any person or entity to expand, limit, modify, or bypass any right,
              obligation, restriction, allocation of responsibility, warranty disclaimer, limitation of liability, or
              remedy established in the MSA or BAA.
            </p>

            <h3>3.3 No Independent Access Entitlement</h3>
            <p>
              Your access to the Platform is contingent on the Customer’s active subscription, the Customer’s
              configuration choices, and the Customer’s designation of you as an Authorized User.
            </p>
            <p>
              Snap may suspend, restrict, modify, or terminate your individual access at the Customer’s direction, upon
              expiration or termination of the Customer’s subscription, or upon discovery of actual or suspected
              violation of these Terms.
            </p>

            <h2>4. Authorized Access and Account Security</h2>
            <h3>4.1 Authorized Scope</h3>
            <p>
              You may access only those Platform functions, records, workflows, employer accounts, patient populations,
              reports, documents, and administrative areas that are made available to you through your assigned role
              and permissions.
            </p>
            <p>
              You must not access, view, use, copy, export, alter, disclose, or attempt to access any data, system,
              account, workflow, or function outside your authorized scope.
            </p>

            <h3>4.2 Credential Security</h3>
            <p>
              Your username, password, multi-factor authentication code, token, device, and other access credentials are
              personal to you and may not be shared with any other person under any circumstances.
            </p>
            <p>All activity performed using your credentials may be attributed to you.</p>

            <h3>4.3 Security Incident Reporting</h3>
            <p>
              You must immediately report any known or suspected unauthorized access, credential compromise, security
              issue, privacy incident, improper disclosure, or other misuse of the Platform to the Customer’s compliance,
              privacy, or security officer and, where appropriate, to Snap through the support channels made available
              by Snap.
            </p>

            <h3>4.4 Access Controls and Audit Controls</h3>
            <p>
              You acknowledge that Platform access controls, authentication controls, and audit logs support compliance
              with applicable security and confidentiality requirements, including HIPAA Security Rule access control and
              audit control requirements where applicable.
            </p>

            <h2>5. Legal Obligations Regarding Health Information</h2>
            <h3>5.1 Protected Health Information</h3>
            <p>
              Protected Health Information or PHI means individually identifiable health information as defined under the
              Health Insurance Portability and Accountability Act of 1996 and the Health Information Technology for
              Economic and Clinical Health Act, together with their implementing regulations (HIPAA).
            </p>

            <h3>5.2 Clinical and Administrative Users</h3>
            <p>If you are a Clinical and Administrative User, you acknowledge and agree that:</p>
            <ul>
              <li>You may access PHI through the Platform on behalf of the Customer.</li>
              <li>
                You are subject to HIPAA workforce member obligations applicable to the Customer under 45 C.F.R. §
                164.530.
              </li>
              <li>
                You must handle PHI only as authorized by the Customer and only in accordance with the Customer’s
                privacy policies, security policies, workforce training, applicable BAA requirements, and applicable
                law.
              </li>
              <li>
                You must limit access, use, disclosure, export, and handling of PHI to the minimum necessary for your
                assigned role and authorized duties, except where a HIPAA exception applies.
              </li>
              <li>
                Unauthorized access, use, disclosure, alteration, or export of PHI may violate federal and state law and
                may subject you and the Customer to civil, criminal, employment, contractual, or professional
                consequences.
              </li>
            </ul>

            <h3>5.3 Employer Portal Users</h3>
            <p>If you are an Employer Portal User, you acknowledge and agree that:</p>
            <ul>
              <li>
                You are not accessing the Platform as a workforce member of the clinic or healthcare provider that is
                the Customer.
              </li>
              <li>
                You are not subject to HIPAA workforce member obligations under 45 C.F.R. § 164.530 solely by virtue of
                accessing the Employer Command Center.
              </li>
              <li>
                You may access only employer-authorized occupational health outputs that the Customer has made available
                to you through the Platform under applicable law.
              </li>
              <li>
                Once occupational health information is made available to you through the Employer Command Center, your
                use and handling of that information may be governed by the Americans with Disabilities Act, DOT
                regulations, OSHA requirements, state employment law, state drug and alcohol testing law, workers’
                compensation law, employee authorization terms, and other applicable federal, state, or local laws.
              </li>
            </ul>

            <h3>5.4 ADA Medical Record Confidentiality</h3>
            <p>
              Employer Portal Users must treat employee medical information accessed through the Platform, including
              work status determinations, fitness-for-duty outcomes, occupational health exam results, medical surveillance
              outputs, and related workforce health information, as confidential medical information.
            </p>
            <p>
              You must maintain such information on separate forms, in separate files, and treat it as a confidential
              medical record as required by the Americans with Disabilities Act, including 42 U.S.C. § 12112(c)(4), where
              applicable.
            </p>
            <p>
              You may disclose such information only to individuals or entities legally permitted to receive it,
              including supervisors or managers who need to know work restrictions or accommodations, first aid or safety
              personnel where emergency treatment may be required, government officials investigating compliance, or as
              otherwise permitted or required by law.
            </p>

            <h3>5.5 ADA Medical Examination Restrictions</h3>
            <p>
              Employer Portal Users are responsible for ensuring that any employer-directed medical examination,
              pre-employment examination, post-offer examination, return-to-duty examination, fitness-for-duty
              examination, or similar workflow conducted or reviewed through the Platform complies with applicable law,
              including ADA restrictions on medical examinations and inquiries.
            </p>
            <p>
              The existence of a workflow, result, document, or data field within the Platform does not mean that the
              employer may lawfully request, review, rely upon, or act on that information in a particular jurisdiction or
              employment context.
            </p>

            <h3>5.6 DOT Confidentiality and DER Obligations</h3>
            <p>
              If you are an Employer Portal User who accesses DOT-regulated drug or alcohol testing information, you
              must comply with 49 C.F.R. Part 40 and all applicable DOT agency regulations.
            </p>
            <p>
              If you are a Designated Employer Representative (DER) or act in coordination with a DER, you are responsible
              for complying with applicable DER obligations, including receiving and acting upon MRO-verified results,
              removing employees from safety-sensitive functions when required, maintaining required records, and limiting
              use and disclosure of DOT testing information to DOT-authorized purposes.
            </p>
            <p>
              DOT test results accessed through the Platform may not be used in any criminal proceeding against the
              tested individual and may not be repurposed for unauthorized employment, disciplinary, or non-DOT purposes.
            </p>

            <h3>5.7 State and Local Law Compliance</h3>
            <p>
              Employer Portal Users are solely responsible for ensuring that use of the Platform and use of any data
              accessed through the Employer Command Center complies with all applicable federal, state, and local laws in
              the jurisdictions where their business is incorporated, licensed, or operates, and in the jurisdictions
              where employees are located or perform work.
            </p>
            <p>This includes, without limitation, laws governing:</p>
            <ul>
              <li>Employer-directed drug and alcohol testing programs.</li>
              <li>Cannabis and marijuana in employment decisions.</li>
              <li>Pre-employment, post-offer, and return-to-duty medical examinations.</li>
              <li>Employee medical record confidentiality.</li>
              <li>Occupational health and medical surveillance requirements.</li>
              <li>
                Adverse employment action based on medical, fitness-for-duty, drug testing, or alcohol testing results.
              </li>
            </ul>
            <p>
              Snap does not provide legal advice regarding applicable state or local law. The existence of a data
              category or workflow within the Platform does not represent Snap’s determination that accessing, using,
              disclosing, retaining, or acting on that data is lawful in your jurisdiction. You are responsible for
              making that determination independently with qualified legal counsel.
            </p>

            <h2>6. Employer Command Center Scope</h2>
            <h3>6.1 Permitted Employer Portal Access</h3>
            <p>
              Employer Portal Users may access only those categories of information that the Customer has configured and
              authorized for employer-facing access through the Employer Command Center.
            </p>
            <p>Permitted employer-facing categories may include, where authorized:</p>
            <ul>
              <li>Scheduling and appointment status.</li>
              <li>Employer-service workflow status.</li>
              <li>Work status summaries.</li>
              <li>DOT medical certificate or card expiration data.</li>
              <li>Drug and alcohol testing results or status.</li>
              <li>Occupational health compliance records.</li>
              <li>Vaccine compliance status.</li>
              <li>Lab results in the employer-service context.</li>
              <li>Historical employer-service records in substantially similar categories.</li>
              <li>Invoices, billing records, and employer-relevant uploaded documents.</li>
            </ul>

            <h3>6.2 Prohibited Employer Portal Access</h3>
            <p>Employer Portal Users may not access or attempt to access:</p>
            <ul>
              <li>Narrative clinical notes.</li>
              <li>Unrestricted patient charts.</li>
              <li>Non-employer-service medical records.</li>
              <li>Medical questionnaire details not expressly authorized for employer release.</li>
              <li>Genetic information, including family medical history.</li>
              <li>Substance use disorder treatment, counseling, or referral records.</li>
              <li>Any information outside the Customer-configured employer-facing scope.</li>
            </ul>

            <h3>6.3 No Clinical Decision-Making</h3>
            <p>
              Employer Portal Users are not authorized to make clinical, diagnostic, treatment, medical necessity,
              impairment rating, or medical causation decisions through the Platform.
            </p>
            <p>
              Work status determinations, medical clearance outcomes, fitness-for-duty conclusions, drug testing
              interpretations, and occupational health outputs are determined by licensed providers, MROs, certified medical
              examiners, or other qualified professionals, as applicable. Snap does not make those determinations.
            </p>

            <h2>7. Acceptable Use</h2>
            <h3>7.1 All Authorized Users</h3>
            <p>You must not:</p>
            <ul>
              <li>Use the Platform for any unlawful, fraudulent, abusive, deceptive, or unauthorized purpose.</li>
              <li>
                Attempt to access data, systems, accounts, network components, administrative tools, or records beyond your
                authorization.
              </li>
              <li>
                Circumvent, disable, impair, or interfere with authentication, access controls, audit logging, security
                controls, monitoring, or Platform restrictions.
              </li>
              <li>
                Introduce malware, viruses, unauthorized scripts, bots, crawlers, scraping tools, or harmful code.
              </li>
              <li>
                Use the Platform to transmit unauthorized communications, spam, harassment, or materials unrelated to
                authorized Platform use.
              </li>
              <li>
                Use the Platform to build, benchmark, train, improve, or support any competing product or service.
              </li>
              <li>
                Copy, scrape, frame, mirror, reverse engineer, decompile, disassemble, extract, or otherwise misuse any
                Platform component, interface, workflow, model, template, or documentation.
              </li>
              <li>Permit another person to use the Platform through your account.</li>
              <li>
                Use the Platform to submit, support, create, or facilitate false, fraudulent, medically unnecessary, or
                improper claims, charges, billing codes, documentation, or reimbursement submissions.
              </li>
              <li>
                Use Platform outputs as a substitute for required professional judgment, legal review, regulatory
                compliance review, medical review, or employer compliance review.
              </li>
            </ul>

            <h3>7.2 Clinical and Administrative Users</h3>
            <p>Clinical and Administrative Users must not use the Platform to:</p>
            <ul>
              <li>Document care outside their professional scope, licensure, authorization, or assigned duties.</li>
              <li>Alter, delete, or falsify clinical, billing, occupational health, or compliance records.</li>
              <li>Submit or support false claims, inaccurate coding, unsupported billing, or medically unnecessary services.</li>
              <li>
                Use lab, drug testing, imaging, clinical decision support, AI, or occupational health outputs without
                appropriate professional review.
              </li>
              <li>
                Interfere with patient access rights, record amendment rights, accounting obligations, or legally required
                record exports.
              </li>
            </ul>

            <h3>7.3 Employer Portal Users</h3>
            <p>Employer Portal Users must not use the Platform to:</p>
            <ul>
              <li>Request, obtain, view, retain, or use genetic information, including family medical history.</li>
              <li>
                Access or attempt to access clinical notes, unrestricted charts, non-employer-service medical records, or
                substance use disorder treatment records.
              </li>
              <li>Use occupational health data for unauthorized employment decisions.</li>
              <li>Use DOT drug or alcohol testing results outside DOT-authorized purposes.</li>
              <li>Use any drug or alcohol testing result in a criminal proceeding against the tested individual.</li>
              <li>
                Use Platform data as a substitute for legal counsel, HR compliance review, DER obligations, OSHA compliance
                obligations, FMCSA obligations, workers’ compensation procedures, or state-law drug testing compliance.
              </li>
              <li>
                Take adverse employment action based on data accessed through the Platform without independently
                confirming that the action complies with applicable law.
              </li>
            </ul>

            <h2>8. AI Features</h2>
            <h3>8.1 Clinical-User-Only Features</h3>
            <p>
              The Platform may include artificial intelligence, machine learning, automation, or drafting features that
              generate chart text, coding suggestions, referral suggestions, compliance prompts, document drafts,
              summaries, or other outputs (AI Output).
            </p>
            <p>
              AI-assisted charting, coding, clinical decision support, and similar features are intended for Clinical and
              Administrative Users only and are not intended for Employer Portal Users.
            </p>

            <h3>8.2 Mandatory Human Review</h3>
            <p>
              AI Output is assistive only. It is not medical advice, legal advice, coding advice, reimbursement advice,
              employment advice, or compliance advice.
            </p>
            <p>
              You must independently review and validate all AI Output before adopting, relying upon, entering into a
              patient record, submitting for billing, transmitting externally, or using in any clinical, coding,
              compliance, work status, or employer-facing context.
            </p>

            <h3>8.3 No Sole Reliance</h3>
            <p>
              You may not use AI Output as the sole basis for any diagnosis, treatment decision, clinical determination,
              work status determination, fitness-for-duty conclusion, billing decision, coding submission, legal
              conclusion, or employment-related decision.
            </p>

            <h3>8.4 Chart Insertion</h3>
            <p>
              Where AI-assisted charting features are available, AI-generated chart text must be reviewed, selected, and
              affirmatively inserted by an authorized Clinical and Administrative User before it becomes part of a
              patient record.
            </p>

            <h3>8.5 Regulatory Purpose of Human Review</h3>
            <p>
              The mandatory human-review requirement is intended to preserve professional judgment, support patient safety,
              reduce documentation risk, and maintain compliance with applicable healthcare software, clinical decision
              support, billing, privacy, and security requirements.
            </p>

            <h2>9. Electronic Prescribing and EPCS</h2>
            <h3>9.1 Clinical-User-Only Functionality</h3>
            <p>
              Electronic prescribing and Electronic Prescribing for Controlled Substances (EPCS) functionality is
              available only to authorized Clinical and Administrative Users whose role, licensure, credentials, identity
              verification, and Customer configuration permit such access.
            </p>
            <p>Employer Portal Users do not have EPCS access.</p>

            <h3>9.2 EPCS Legal Obligations</h3>
            <p>
              If your role includes access to EPCS functionality, you acknowledge that EPCS is subject to DEA regulations,
              applicable state pharmacy board rules, prescriber identity proofing requirements, two-factor authentication
              requirements, audit logging requirements, and the Customer’s prescribing policies.
            </p>
            <p>You are solely responsible for complying with all prescriber obligations applicable to your use of EPCS functionality.</p>

            <h3>9.3 Misuse</h3>
            <p>
              Misuse of prescribing or EPCS functionality may constitute a violation of federal or state law and may
              result in suspension of access, reporting to the Customer, regulatory reporting, professional discipline,
              civil liability, or criminal penalties.
            </p>

            <h2>10. Laboratory, CLIA, and Diagnostic Information</h2>
            <h3>10.1 Platform Is Not a Laboratory</h3>
            <p>
              Snap is not a clinical laboratory, does not independently perform laboratory testing, and does not provide
              CLIA-certified laboratory services through the Platform.
            </p>

            <h3>10.2 Customer Responsibility</h3>
            <p>
              Customers and their laboratory partners are responsible for compliance with the Clinical Laboratory
              Improvement Amendments, applicable state laboratory laws, specimen collection requirements, result reporting
              requirements, personnel qualifications, quality control, and laboratory certification obligations.
            </p>

            <h3>10.3 Result Interpretation</h3>
            <p>
              Laboratory, drug testing, imaging, diagnostic, and similar results displayed or stored in the Platform must
              be reviewed and interpreted by appropriately qualified personnel. The Platform does not independently
              validate, certify, interpret, or guarantee the clinical accuracy of such results.
            </p>

            <h2>11. DOT, NRCME, FMCSA, and Occupational Health Workflows</h2>
            <h3>11.1 NRCME Medical Examiner Obligations</h3>
            <p>
              If you are a certified medical examiner registered on the FMCSA National Registry of Certified Medical
              Examiners, your obligation to complete, maintain, and transmit DOT physical examination results and related
              records as required by FMCSA regulations is independent of these Terms.
            </p>
            <p>
              The Platform may facilitate DOT physical examination and medical certificate workflows, but it is not a
              substitute for your independent regulatory obligations.
            </p>

            <h3>11.2 FMCSA Reporting and Export</h3>
            <p>
              Nothing in these Terms restricts a certified medical examiner’s ability or obligation to access, export,
              transmit, or report DOT physical examination data to the FMCSA National Registry or other legally authorized
              recipient as required by applicable law.
            </p>

            <h3>11.3 Employer FMCSA Obligations</h3>
            <p>
              Employer Portal Users who access DOT medical certificate information, DOT card expiration data, or driver
              qualification information are independently responsible for compliance with FMCSA obligations, including
              driver qualification file requirements and the obligation not to permit a commercial motor vehicle operator to
              operate without a current, valid medical certificate.
            </p>
            <p>
              DOT medical certificate information displayed in the Platform reflects information made available through
              the Customer’s records and does not substitute for verification through the driver’s physical certificate,
              applicable state systems, or the FMCSA National Registry.
            </p>

            <h2>12. Drug and Alcohol Testing; Substance Use Disorder Records</h2>
            <h3>12.1 Employer-Directed Testing</h3>
            <p>
              Drug and alcohol testing results accessible through the Platform, including DOT and non-DOT employer-directed
              testing, are generated through employer-authorized occupational health testing workflows and are not
              substance abuse treatment program records in that context.
            </p>

            <h3>12.2 Substance Use Disorder Records Outside Platform Scope</h3>
            <p>
              The Platform does not maintain records from substance abuse treatment, counseling, or referral programs.
              Users may not use the Platform in connection with any substance abuse diagnosis, treatment, counseling, or
              referral program.
            </p>
            <p>
              Any clinic seeking to use the Platform to maintain records of a federally assisted substance abuse program
              must contact Snap before doing so. Such use is outside the intended scope of the Platform and is not
              currently supported.
            </p>

            <h3>12.3 Employer Command Center Limitation</h3>
            <p>
              Records from substance abuse treatment programs are outside the scope of the Employer Command Center and are
              not accessible through the Employer Command Center.
            </p>
            <p>
              If an Employer Portal User receives information through the Platform that the user believes may relate to an
              employee’s substance use disorder treatment, counseling, or referral records, the user must not use or
              disclose that information and must notify the Customer immediately.
            </p>

            <h2>13. Genetic Information</h2>
            <h3>13.1 GINA Compliance</h3>
            <p>
              Employer Portal Users must not use the Platform or Employer Command Center to request, obtain, view, retain,
              disclose, or use genetic information about employees, applicants, or family members, including family
              medical history.
            </p>

            <h3>13.2 Safe Harbor</h3>
            <p>
              Employer Portal Users are responsible for ensuring that any employer-directed medical examination,
              occupational health questionnaire, or health history request conducted through or in connection with the
              Customer includes appropriate safeguards to avoid requesting genetic information, including a GINA-compliant
              safe harbor notice where applicable.
            </p>

            <h3>13.3 Improper Receipt</h3>
            <p>
              If you receive information through the Platform that you believe may constitute genetic information, you must
              not use or disclose that information and must notify the Customer immediately.
            </p>

            <h2>14. OSHA, Medical Surveillance, and Employer Recordkeeping</h2>
            <h3>14.1 OSHA Medical Records</h3>
            <p>
              Occupational health records accessible through the Employer Command Center, including audiometric testing
              results, respirator clearance determinations, medical surveillance outcomes, exposure-related records, and
              similar workforce health records, may constitute employee medical records subject to OSHA requirements,
              including 29 C.F.R. § 1910.1020 and applicable OSHA substance-specific standards.
            </p>

            <h3>14.2 Employer Retention Responsibility</h3>
            <p>
              Employer Portal Users and their organizations are responsible for maintaining independent copies of records
              that they are legally required to retain, including records subject to OSHA-mandated retention periods.
            </p>
            <p>
              Platform access is contingent on the Customer’s active subscription and configuration. Cancellation,
              expiration, suspension, or lapse of the Customer’s subscription may affect employer access to historical
              records.
            </p>

            <h3>14.3 OSHA Recordkeeping</h3>
            <p>
              The Platform does not replace an employer’s OSHA recordkeeping obligations, including OSHA injury and illness
              recordkeeping obligations, medical surveillance obligations, employee access rights, privacy case handling, or
              retention obligations.
            </p>

            <h2>15. Workers’ Compensation, FCRA, and Employment Decisions</h2>
            <h3>15.1 Workers’ Compensation</h3>
            <p>
              The Platform may support occupational health workflows that relate to employer services, workforce compliance,
              or workers’ compensation-adjacent information. The Platform does not replace state workers’ compensation
              procedures, claim handling obligations, record access rights, carrier requirements, or employer
              responsibilities under applicable workers’ compensation law.
            </p>

            <h3>15.2 FCRA</h3>
            <p>
              If drug testing results, occupational health information, background-related information, or similar data
              accessed through the Platform is communicated through or in connection with a consumer reporting agency as
              defined under the Fair Credit Reporting Act, Employer Portal Users and their organizations are responsible for
              complying with all applicable FCRA requirements, including authorization, disclosure, pre-adverse action,
              adverse action, and notice requirements.
            </p>

            <h3>15.3 Employment Decisions</h3>
            <p>
              Snap does not make hiring, placement, removal, accommodation, return-to-duty, disciplinary, or adverse
              employment decisions. Employer Portal Users and their organizations are solely responsible for ensuring that
              employment decisions based on information accessed through the Platform comply with applicable law.
            </p>

            <h2>16. Access Logging, Monitoring, and Audit</h2>
            <h3>16.1 Logged Activity</h3>
            <p>All activity on the Platform is logged and monitored. Activity logs may include:</p>
            <ul>
              <li>
                Login identity, IP address, device information, browser information, authentication events, and session
                duration.
              </li>
              <li>
                Records, employer accounts, patient files, documents, reports, workflows, and data elements accessed,
                viewed, created, modified, exported, printed, or deleted.
              </li>
              <li>
                Feature usage, query history, document generation, report generation, administrative actions, and
                configuration changes.
              </li>
              <li>AI Feature interactions and AI Output selection events.</li>
              <li>Failed login attempts, MFA verification events, anomalous access patterns, and security events.</li>
            </ul>

            <h3>16.2 Use of Logs</h3>
            <p>
              Logs are maintained for security, compliance, support, audit, legal, regulatory, HIPAA, SOC 2, incident
              response, and Customer oversight purposes.
            </p>

            <h3>16.3 Customer Administrator Access</h3>
            <p>
              For Clinical and Administrative Users, the Customer’s authorized administrators may access logs of your
              Platform activity.
            </p>
            <p>
              For Employer Portal Users, the clinic or healthcare organization that is the Customer may access logs of your
              Employer Command Center activity. Your employer organization may also receive access to certain activity
              information if made available by the Customer or authorized under applicable configuration, policy, or law.
            </p>

            <h3>16.4 Legal Process and Regulatory Requests</h3>
            <p>
              Activity logs may be used, retained, or produced in response to valid legal process, subpoena, court order,
              regulatory inquiry, government audit, security investigation, Customer audit request, or other legally
              authorized request.
            </p>
            <p>By using the Platform, you consent to this logging and monitoring.</p>

            <h2>17. Intellectual Property and Data Portability</h2>
            <h3>17.1 Snap Intellectual Property</h3>
            <p>
              The Platform, including all software, source code, object code, architecture, interfaces, layouts,
              workflows, templates, forms, documentation, AI models, algorithms, configurations, designs, user experience
              elements, and related materials, is owned by Snap or its licensors.
            </p>
            <p>Your access to the Platform does not grant you any ownership interest in Snap intellectual property.</p>

            <h3>17.2 Restrictions</h3>
            <p>
              You may not copy, reverse engineer, decompile, disassemble, frame, scrape, mirror, extract, reproduce,
              modify, distribute, commercialize, or create derivative works from any component of the Platform except as
              expressly permitted through Platform features and your authorized use.
            </p>

            <h3>17.3 Data Access and Portability Carve-Out</h3>
            <p>
              Nothing in this Section restricts any Authorized User’s ability to access, export, or transmit data through
              Platform features and functions specifically provided for that purpose, including patient record export,
              employer-service data export, and other data portability tools made available within the Platform.
            </p>
            <p>
              Authorized Users who are licensed healthcare providers retain all rights and obligations to access and
              transmit patient records as required by applicable clinical, licensing, and regulatory requirements.
            </p>

            <h2>18. Availability, Maintenance, and Support</h2>
            <h3>18.1 Availability</h3>
            <p>
              Platform availability, maintenance, service levels, support commitments, and remedies are governed by the
              MSA and applicable Order Form between Snap and the Customer.
            </p>

            <h3>18.2 Maintenance and Interruptions</h3>
            <p>
              The Platform may be unavailable from time to time due to scheduled maintenance, emergency maintenance,
              security response, service interruptions, third-party service failures, connectivity issues, force majeure
              events, or other circumstances.
            </p>

            <h3>18.3 No Individual SLA</h3>
            <p>
              These Terms do not create any individual service level agreement, uptime warranty, support entitlement, or
              direct remedy in favor of an individual Authorized User.
            </p>

            <h2>19. No Warranties to Individual Users</h2>
            <p>
              The Platform is provided to you as an Authorized User under the Customer’s subscription and subject to the
              MSA.
            </p>
            <p>
              To the maximum extent permitted by law, Snap makes no separate or independent warranties to you as an
              individual Authorized User, including any warranty of uninterrupted access, fitness for a particular
              purpose, accuracy of outputs, suitability for employment decisions, regulatory compliance, clinical
              outcome, or non-infringement.
            </p>

            <h2>20. Limitation of Liability to Individual Users</h2>
            <p>Snap’s obligations and liability run to the Customer under the MSA, not to you as an individual Authorized User.</p>
            <p>
              To the maximum extent permitted by applicable law, Snap is not directly liable to you for any loss, damage,
              harm, employment action, clinical outcome, regulatory consequence, disciplinary action, business loss, data
              access issue, or other claim arising from your use of or inability to use the Platform.
            </p>
            <p>
              Any claims arising from Platform use must be directed to the Customer, which retains any remedies available
              against Snap under the MSA.
            </p>

            <h2>21. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Snap, its affiliates, officers, directors, employees,
              contractors, licensors, and service providers from and against any claims, damages, liabilities, losses,
              costs, and expenses, including reasonable attorneys’ fees, arising out of or related to:
            </p>
            <ul>
              <li>Your unauthorized or improper use of the Platform.</li>
              <li>Your violation of these Terms.</li>
              <li>Your violation of applicable law.</li>
              <li>Your unauthorized access, use, disclosure, export, retention, or alteration of data.</li>
              <li>Your credential sharing or failure to protect credentials.</li>
              <li>
                Your misuse of AI Output, clinical tools, billing tools, employer-facing outputs, drug testing data, DOT
                data, or occupational health data.
              </li>
              <li>
                Any employment, clinical, legal, regulatory, or compliance decision you make based on Platform data or
                outputs.
              </li>
            </ul>

            <h2>22. Termination and Suspension of Access</h2>
            <p>Snap may suspend, restrict, or terminate your access to the Platform:</p>
            <ul>
              <li>At the Customer’s direction.</li>
              <li>Upon termination, expiration, or suspension of the Customer’s subscription.</li>
              <li>
                If Snap reasonably suspects unauthorized access, credential compromise, security risk, privacy incident, or
                violation of these Terms.
              </li>
              <li>If required by law, regulation, court order, government request, or security obligation.</li>
              <li>If your role, employment, authorization, or Customer-configured access ends.</li>
            </ul>
            <p>Snap is not required to provide advance notice to you before suspending or terminating individual access.</p>

            <h2>23. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard to conflict-of-law principles.
            </p>
            <p>
              To the extent an individual dispute between you and Snap is not governed by the MSA or otherwise required to
              be handled through the Customer, any such dispute must be brought exclusively in the state or federal courts
              located in Los Angeles County, California, unless applicable law requires otherwise.
            </p>

            <h2>24. Changes to These Terms</h2>
            <p>
              Snap may update these Terms from time to time. If Snap makes material changes, Snap will provide notice
              through an in-platform notification at next login, by email to the address associated with your account, or
              through another reasonable notice mechanism.
            </p>
            <p>Your continued use of the Platform after the effective date of updated Terms constitutes acceptance of the updated Terms.</p>
            <p>Snap may retain records showing which version of the Terms you accepted and when.</p>

            <h2>25. Miscellaneous</h2>
            <h3>25.1 Severability</h3>
            <p>If any provision of these Terms is found unenforceable, the remaining provisions remain in full force and effect.</p>

            <h3>25.2 No Waiver</h3>
            <p>
              Snap’s failure to enforce any provision of these Terms does not waive Snap’s right to enforce that provision
              later.
            </p>

            <h3>25.3 Assignment</h3>
            <p>You may not assign or transfer your rights or obligations under these Terms.</p>

            <h3>25.4 Entire User-Level Agreement</h3>
            <p>
              These Terms constitute the entire user-level agreement between you and Snap regarding your individual conduct
              as an Authorized User.
            </p>

            <h2>26. Contact Information</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p>
              <strong>Snap Solutions Inc.</strong>
              <br />
              3838 Del Amo Blvd., Suite 202
              <br />
              Torrance, California 90503
              <br />
              Email: <a href="mailto:support@snapsolutions.com">support@snapsolutions.com</a>
            </p>
          </div>
        </section>

        <footer className="terms-footer-strip">
          <strong>Snap Solutions Inc.</strong> &nbsp;·&nbsp; <strong>Terms of Use</strong> &nbsp;·&nbsp;{" "}
          <strong>Confidential</strong>
        </footer>
      </div>
    </div>
  );
}
